import { useState, useEffect } from "react"
import { selectCurrentId } from "../auth/authSlice"
import { selectCrm } from "../crm/crmSlice"
import { useSelector, useDispatch } from "react-redux"
import { Alert, Spin, Progress, Modal } from "antd"
import { useGetDocuSignAccountMutation, useGetDocuSignProcessingRequestMutation, useRequestBulkDocusignMutation, useCompleteDocusignRequestMutation, useRequestIndividualDocusignMutation } from "./formApiSlice"
import { CheckCircleFilled, DoubleRightOutlined, CloseCircleFilled, ExclamationCircleOutlined } from "@ant-design/icons"
import { showSpinner, hideSpinner } from "../../components/componentSlice"

import "./FormDocuSign.css"
import FormDocuSignInvalidClients from "./FormDocuSignInvalidClients"

const FormDocuSign = ( {isBulk, clientId, invalidReasons } ) => {
    const [errorMessage, setErrorMessage] = useState(null)
    const [docusignAccount, setDocusignAccount] = useState(null)
    const [isConnectStarted, setConnectStarted] = useState(false)
    const [isConnectionChecked, setConnectionChecked] = useState(false)
    const [activeDocusignRequest, setActiveDocusignRequest] = useState(null)
    const [isInvalidClientsModalOpen, setIsInvalidClientsModalOpen] = useState(false)

    const userId = useSelector(selectCurrentId)
    const crmId = useSelector(selectCrm)

    const [getDocuSignAccount, { isLoading }] = useGetDocuSignAccountMutation()
    const [getDocuSignProcessingRequest, { isLoading: isGetProcessingRequestLoading }] = useGetDocuSignProcessingRequestMutation()
    const [requestBulkDocusign, { isLoading: isRequestBulkDocusignLoading }] = useRequestBulkDocusignMutation()
    const [completeDocusignRequest, { isLoading: isCompleteDocusignRequestLoading }] = useCompleteDocusignRequestMutation()
    const [requestIndividualDocusign, { isLoading: isRequestIndividualDocusignLoading }] = useRequestIndividualDocusignMutation()


    const dispatch = useDispatch()

    useEffect(() => {
        if (isRequestBulkDocusignLoading || isCompleteDocusignRequestLoading || isRequestIndividualDocusignLoading) {
            dispatch(showSpinner())
        } else {
            dispatch(hideSpinner())
        }
    }, [isRequestBulkDocusignLoading, isCompleteDocusignRequestLoading, isRequestIndividualDocusignLoading])

    useEffect(() => {
        checkAccountConnected(true)
        getProcessingBulkRequest()
    }, [])

    useEffect(() => {
        if(activeDocusignRequest && activeDocusignRequest.status === "CREATING") {
            const interval = setInterval(getProcessingBulkRequest, 5000)
            return () => clearInterval(interval)
        }
    }, [activeDocusignRequest])

    const getProcessingBulkRequest = async () => {
        try {
            const response = await getDocuSignProcessingRequest({"userId": userId, "type": isBulk ? "BULK" : "INDIVIDUAL", "clientId": clientId}).unwrap()
            if(response) {
                setActiveDocusignRequest(response)
            }
        } catch(err) {
            if(!err.status || err.status === "FETCH_ERROR") {
                setErrorMessage("No Server Response")
            } else {
                setErrorMessage(err.data?.detail)
            }
        }
    }

    const checkAccountConnected = async (initialCheck) => {
        try {
            const response = await getDocuSignAccount(userId).unwrap()
            setDocusignAccount(response)
            setConnectionChecked(true)
            
        } catch(err) {
            if(!err.status || err.status === "FETCH_ERROR") {
                setErrorMessage("No Server Response")
            } else {
                setErrorMessage(err.data?.detail)
            }
        }
    }

    const handleRequestDocusign = async () => {
        try {
            if(isBulk) {
                const response = await requestBulkDocusign({"crmId": crmId, "userId":userId}).unwrap()
                if(response) {
                    setActiveDocusignRequest(response)
                }
            } else {
                const response = await requestIndividualDocusign({"crmId": crmId, "userId":userId, "clientId": clientId}).unwrap()
                if(response) {
                    setActiveDocusignRequest(response)
                }
            }
        } catch(err) {
            if(!err.status || err.status === "FETCH_ERROR") {
                setErrorMessage("No Server Response")
            } else {
                setErrorMessage(err.data?.detail)
            }
        }
    }

    const completeBulkRequest = async (id) => {
        try {
            await completeDocusignRequest(id).unwrap()
            setActiveDocusignRequest(null)
        } catch(err) {
            if(!err.status || err.status === "FETCH_ERROR") {
                setErrorMessage("No Server Response")
            } else {
                setErrorMessage(err.data?.detail)
            }
        }
    }

    const sendAgain = async (id) => {
        try {
            await completeDocusignRequest(id).unwrap()
            handleRequestDocusign()
        } catch(err) {
            if(!err.status || err.status === "FETCH_ERROR") {
                setErrorMessage("No Server Response")
            } else {
                setErrorMessage(err.data?.detail)
            }
        }
    }

    const createAccountNotConnectedView = () => {
        return (
            <div className="docusign-request-card">
                <Alert
                    showIcon
                    description="DocuSign account has not been connected yet. Contact admin to connect your account."
                    type="warning"
                    banner
                />
                <div className="docusign-button">
                    <button className="crm-button" disabled>
                        Send DocuSign
                    </button>     
                </div>                 
            </div>
        )
    }

    const createAccountConnectedView = () => {
        return (
            <div className="docusign-request-card">
                <div className="docusign-account-info">
                    <CheckCircleFilled />
                    <span>Connected as: {`${docusignAccount.email}`}</span>
                </div>
                {activeDocusignRequest ? createActiveDocusignView() : createSendDocusignView()}
            </div>
        )
    }

    const createActiveDocusignView = () => {
        return (
            <>
                <div className="docusign-request-header">
                    <div className="docusign-request-id">
                        <p>Id: {activeDocusignRequest.id}</p>
                    </div>
                </div>
                <div className="docusign-request-body">
                    <Progress percent={getPercentage(activeDocusignRequest.processedClients, activeDocusignRequest.totalClients)} status="active" strokeColor={{ from: '#108ee9', to: '#4cb121' }} />
                    {
                        activeDocusignRequest.status === 'CREATING' ? 
                            <>
                                <Spin size="large" description="Loading" spinning={true} />
                                <p>Creating DocuSign envelopes...</p>
                            </>
                        :
                            <>
                                <CheckCircleFilled style={{ color: '#4cb121', fontSize: '50px' }} />
                                <p>Request completed.</p>
                                {activeDocusignRequest.invalidClients.length > 0 &&
                                    <p>Some envelope(s) are not sent. Please check for the reason and try again.</p>
                                }
                            </>
                    }
                </div>
                {
                    activeDocusignRequest.invalidClients.length > 0 &&
                    <div className="docusign-invalid-client">
                        <p>{activeDocusignRequest.invalidClients.length} envelope(s) not sent.</p>
                        <button className="invalid-client-button" onClick={() => setIsInvalidClientsModalOpen(true)}>
                            <span>Show more details</span>
                            <DoubleRightOutlined />
                        </button>
                        <Modal open={isInvalidClientsModalOpen} onCancel={() => setIsInvalidClientsModalOpen(false)} footer={[]}>
                            <FormDocuSignInvalidClients invalidClients={activeDocusignRequest.invalidClients} />
                        </Modal>
                    </div>
                }
                {
                    activeDocusignRequest.status === "DOCUSIGN_CREATED" && 
                    <div className="docusign-control-container">
                        <div className="docusign-control-buttons">
                            <button className="crm-button crm-btn-primary" onClick={() => sendAgain(activeDocusignRequest.id)}>Request Again</button>
                            <button className="crm-button" onClick={() => completeBulkRequest(activeDocusignRequest.id)}>Complete</button>
                        </div>
                    </div>
                }
            </> 
        )
    }

    const createSendDocusignView = () => {
        return (
            <>
                {isBulk &&
                    <Alert
                        showIcon
                        description="Send DocuSign for all clients. Envelopes will be automatically sent to each clients."
                        type="info"
                        banner
                    /> 
                }

                {!isBulk && invalidReasons?.length === 0 && 
                    <Alert
                        showIcon
                        description="DocuSign envelope is ready to be sent to the client."
                        type="info"
                        banner
                    /> 
                }
                
                {invalidReasons?.length > 0 && createReasons()}
                <div className="docusign-button">
                    <button className="crm-button" onClick={handleRequestDocusign} disabled={invalidReasons?.length > 0}>
                        Send DocuSign
                    </button>     
                </div>                     
            </>
        )
    }

    const getPercentage = (processed, total) => {
        if (total === 0) return 0
        return Math.round((processed / total) * 100)
    }

    const createReasons = () => {
        return (
            <div className="invalid-reasons-container">
                <div className="invalid-reasons-desc">
                    <CloseCircleFilled />
                    <span>Please fix the following issues before you can proceed.</span>
                </div>
                <div className="invalid-reasons-table">
                    <table>
                        {invalidReasons.map(invalidReason => <tr><td><ExclamationCircleOutlined /> {invalidReason}</td></tr>)}
                    </table>
                </div>
            </div>
            
        )
    }

    return (
        <div className="table-page-container">
            {errorMessage && 
                <div className="support-error">
                    <p>{errorMessage}</p>
                </div>
            }
            <div className="forms-header">
                <div className='top-header'>
                    <header>{isBulk ? "Bulk Docusign Request" : "Request DocuSign"}</header>
                </div>
            </div>
            {
                !isConnectionChecked ? null 
                : (isConnectionChecked && !docusignAccount) ? createAccountNotConnectedView() 
                : createAccountConnectedView()
            }
        </div>
    )
}

export default FormDocuSign